import { useState, useEffect, useCallback } from "react";
import { supabase } from "../../supabase";
import Select from "../../components/Select";
import ContactReplyPanel from "./ContactReplyPanel";

const TYPE_LABEL = { bug: "버그 신고", feedback: "피드백", other: "기타" };
const TYPE_COLOR = { bug: "#e53e3e", feedback: "#C8813F", other: "#888" };
const TYPE_BG   = { bug: "#fff0f0", feedback: "#eff6ff", other: "#f5f5f5" };

export default function AdminContact() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [replyBusy, setReplyBusy] = useState(false);
  const [drafts, setDrafts] = useState({});
  const PAGE_SIZE = 20;

  const loadData = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    let q = supabase
      .from("contact_inquiries")
      .select("*,contact_inquiry_replies(status)", { count: "exact" })
      .order("created_at", { ascending: false })
      .range((page - 1) * PAGE_SIZE, page * PAGE_SIZE - 1);

    if (filterType) q = q.eq("inquiry_type", filterType);
    if (filterStatus) q = q.eq("status", filterStatus);

    const { data, count, error } = await q;
    if (!error) { setItems(data || []); setTotal(count || 0); }
    else setLoadError("문의 목록을 불러오지 못했습니다. 연결 상태와 답변 기능의 DB 적용 여부를 확인해 주세요.");
    setLoading(false);
  }, [page, filterType, filterStatus]);

  useEffect(() => { loadData(); }, [loadData]);

  async function markRead(id) {
    const { error } = await supabase.from("contact_inquiries").update({ status: "read" }).eq("id", id);
    if (error) { setLoadError("확인 상태를 저장하지 못했습니다."); return; }
    setItems((prev) => prev.map((i) => i.id === id ? { ...i, status: "read" } : i));
    setSelected((s) => s?.id === id ? { ...s, status: "read" } : s);
  }

  function replySent(id) {
    setItems((rows) => rows.map((row) => row.id === id ? { ...row, contact_inquiry_replies: [{ status: "sent" }] } : row));
  }
  function closeDetail() {
    if (!replyBusy) setSelected(null);
  }

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const unreadCount = items.filter((i) => i.status === "unread").length;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 4 }}>
        <h2 style={{ margin: 0 }}>문의 관리</h2>
        {unreadCount > 0 && (
          <span style={{ background: "#e53e3e", color: "#fff", fontSize: 12, fontWeight: 700, borderRadius: 999, padding: "2px 8px" }}>
            미확인 {unreadCount}
          </span>
        )}
      </div>
      <p style={{ color: "#aaa", fontSize: 13, marginBottom: 20 }}>총 {total}건</p>

      {/* 필터 */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <Select value={filterType} onChange={(e) => { setFilterType(e.target.value); setPage(1); }}
          style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #e0d8d0", fontSize: 13, background: "#fff" }}>
          <option value="">전체 유형</option>
          <option value="bug">버그 신고</option>
          <option value="feedback">피드백</option>
          <option value="other">기타</option>
        </Select>
        <Select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
          style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #e0d8d0", fontSize: 13, background: "#fff" }}>
          <option value="">전체 상태</option>
          <option value="unread">미확인</option>
          <option value="read">확인</option>
        </Select>
      </div>

      {loadError && <p role="alert" style={{ color: "#a43b24" }}>{loadError}</p>}
      {/* 테이블 */}
      <div style={{ overflowX: "auto", border: "1px solid #f0ece8", borderRadius: 12 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "#faf7f4" }}>
              <th style={th}>날짜</th>
              <th style={th}>이메일</th>
              <th style={th}>유형</th>
              <th style={th}>내용 요약</th>
              <th style={th}>상태</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} style={{ textAlign: "center", padding: 40, color: "#aaa" }}>불러오는 중...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={5} style={{ textAlign: "center", padding: 40, color: "#aaa" }}>문의가 없어요.</td></tr>
            ) : items.map((item) => (
              <tr key={item.id}
                style={{ borderTop: "1px solid #f0ece8", cursor: "pointer", background: item.status === "unread" ? "#fffcf8" : "#fff", transition: "background 0.15s" }}
                onMouseEnter={(e) => e.currentTarget.style.background = "#faf7f4"}
                onMouseLeave={(e) => e.currentTarget.style.background = item.status === "unread" ? "#fffcf8" : "#fff"}
                onClick={() => { setSelected(item); if (item.status === "unread") markRead(item.id); }}>
                <td style={{ ...td, color: "#888", whiteSpace: "nowrap" }}>
                  {new Date(item.created_at).toLocaleDateString("ko-KR", { timeZone: "Asia/Seoul" })}
                </td>
                <td style={td}>{item.user_email}</td>
                <td style={td}>
                  <span style={{
                    color: TYPE_COLOR[item.inquiry_type], background: TYPE_BG[item.inquiry_type],
                    fontWeight: 600, fontSize: 12, padding: "3px 8px", borderRadius: 6
                  }}>
                    {TYPE_LABEL[item.inquiry_type]}
                  </span>
                </td>
                <td style={{ ...td, maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#555" }}>
                  {item.message}
                </td>
                <td style={td}>
                  {item.contact_inquiry_replies?.some((r) => r.status === "sent") ? (
                    <span style={{ padding: "3px 8px", borderRadius: 6, fontSize: 12, fontWeight: 600, background: "#f4eee7", color: "#61462e" }}>답변 완료</span>
                  ) : item.status === "unread" ? (
                    <span style={{ padding: "3px 8px", borderRadius: 6, fontSize: 12, fontWeight: 600, background: "#fef3cd", color: "#856404" }}>미확인</span>
                  ) : (
                    <span style={{ padding: "3px 8px", borderRadius: 6, fontSize: 12, fontWeight: 600, background: "#e8f5e9", color: "#2e7d32" }}>확인</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <div style={{ display: "flex", gap: 6, marginTop: 16, justifyContent: "center" }}>
          {Array.from({ length: totalPages }, (_, i) => (
            <button key={i + 1} onClick={() => setPage(i + 1)}
              style={{
                padding: "5px 12px", borderRadius: 8, border: "1px solid #e0d8d0",
                background: page === i + 1 ? "#3e2210" : "#fff",
                color: page === i + 1 ? "#fff" : "#555",
                fontSize: 13, cursor: "pointer"
              }}>
              {i + 1}
            </button>
          ))}
        </div>
      )}

      {/* 상세 모달 */}
      {selected && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
          >
          <div role="dialog" aria-modal="true" aria-label="문의 상세" style={{ background: "#fff", borderRadius: 20, padding: 32, maxWidth: 600, width: "100%", maxHeight: "90vh", overflowY: "auto", position: "relative", boxShadow: "0 20px 60px rgba(0,0,0,0.15)" }}>

            {/* 닫기 */}
            <button aria-label="문의 닫기" disabled={replyBusy} onClick={closeDetail}
              style={{ position: "absolute", top: 16, right: 18, background: "none", border: "none", fontSize: 18, cursor: "pointer", color: "#bbb", lineHeight: 1 }}>✕</button>

            {/* 헤더 */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
              <span style={{
                color: TYPE_COLOR[selected.inquiry_type], background: TYPE_BG[selected.inquiry_type],
                fontWeight: 700, fontSize: 13, padding: "4px 10px", borderRadius: 8
              }}>
                {TYPE_LABEL[selected.inquiry_type]}
              </span>
              {selected.status === "unread" ? (
                <span style={{ fontSize: 12, fontWeight: 600, color: "#856404", background: "#fef3cd", padding: "3px 8px", borderRadius: 6 }}>미확인</span>
              ) : (
                <span style={{ fontSize: 12, fontWeight: 600, color: "#2e7d32", background: "#e8f5e9", padding: "3px 8px", borderRadius: 6 }}>확인</span>
              )}
            </div>

            {/* 메타 정보 */}
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 20, padding: "14px 16px", background: "#faf7f4", borderRadius: 12 }}>
              <div style={{ display: "flex", gap: 8, fontSize: 13 }}>
                <span style={{ color: "#aaa", minWidth: 50 }}>이메일</span>
                <span style={{ color: "#2A1C13", fontWeight: 500 }}>{selected.user_email}</span>
              </div>
              <div style={{ display: "flex", gap: 8, fontSize: 13 }}>
                <span style={{ color: "#aaa", minWidth: 50 }}>접수일</span>
                <span style={{ color: "#555" }}>{new Date(selected.created_at).toLocaleString("ko-KR", { timeZone: "Asia/Seoul" })}</span>
              </div>
            </div>

            {/* 내용 */}
            <div style={{ fontSize: 14, lineHeight: 1.75, whiteSpace: "pre-wrap", color: "#2A1C13", padding: "16px", background: "#f8f6f3", borderRadius: 12 }}>
              {selected.message}
            </div>

            <ContactReplyPanel key={selected.id} inquiry={selected} draft={drafts[selected.id]}
              onDraft={(draft) => setDrafts((prev) => ({ ...prev, [selected.id]: draft }))}
              onBusy={setReplyBusy} onSent={() => replySent(selected.id)} />

            {/* 확인 버튼 */}
            {selected.status === "unread" && (
              <button onClick={() => markRead(selected.id)}
                style={{ marginTop: 16, width: "100%", padding: "11px", background: "#3e2210", color: "#fff", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
                확인 처리
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const th = { padding: "11px 14px", fontWeight: 600, color: "#888", whiteSpace: "nowrap", textAlign: "left", fontSize: 12 };
const td = { padding: "12px 14px", color: "#2A1C13" };
